import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Box, Button, ButtonBase, Card, CardContent, Chip, MenuItem, Skeleton, Tab, Tabs, TextField, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import BusinessIcon from '@mui/icons-material/Business';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ThunderstormOutlinedIcon from '@mui/icons-material/ThunderstormOutlined';
import { useSnackbar } from 'notistack';
import PageTitle from '../../common/PageTitle';
import ProzoDataGrid from '../../common/ProzoDataGrid';
import AwbLinkCell from '../../common/AwbLinkCell';
import ApiNeededNotice from '../../common/ApiNeededNotice';
import { apiConstants, downloadZip, getGeneric, postGeneric } from '../../common/constants';
import { pageContainerSx, tabBarSx } from '../../styles/patterns';
import { formatDate } from '../../helpers/UtilityHelper';
import { getMerchant } from '../../helpers/ReduxHelpers';
import { awbTextSx } from '../orders/list2/cellStyles';
import { saveCsvAsXlsx } from '../appointments/shared/exportHelpers';
import usePtlApi from './usePtlApi';
import AgeingMatrix from './AgeingMatrix';
import { EDD_BUCKETS, EDD_BUCKET_LABEL, EDD_OWNERS, EDD_OWNER_KEYS, EDD_TABS, errorText, fromUtc } from './constants';

/*
 * EDD breaches for the logged-in merchant, read-only. Same layout as the Admin Panel's EDD Breach Attribution
 * (pages/ptl-edd-breach/EddBreachAttribution.js): tabs → attribution tiles → ageing matrix → filters → list.
 * Left out on purpose (admin only): merchant / courier / pending-with / assigned-to filters, the pending-with and
 * owner columns, every action (attribute, close, reopen), the case drawer, KAM mapping and copy escalation.
 * Kept from the merchant page: the Pickup → Drop column, the AWB link to order details, the XLSX export.
 */

const BASE = apiConstants.PTL_EDD_BREACH_MERCHANT;
const EMPTY_FILTERS = { owner: '', bucket: '', q: '' };
const fmtD = (d) => (d ? formatDate(fromUtc(d), 'dd MMM yyyy') : '—');

export function OwnerChip({ owner }) {
  const o = EDD_OWNERS[owner || 'NONE'];
  return (
    <Box component='span' sx={{
      display: 'inline-block', px: 1, py: 0.25, borderRadius: '4px', fontSize: 12, fontWeight: 700,
      color: o.color, bgcolor: o.bg, border: `1px solid ${owner ? o.bg : '#FFA48D'}`, whiteSpace: 'nowrap',
    }}>
      {o.short}
    </Box>
  );
}

/** "+3d past EDD" (undelivered, red) or "+3d late" (delivered, amber), as the Admin Panel. */
function LateTag({ breachType, lateDays }) {
  if (!lateDays) return null;
  const undelivered = breachType === 'U';
  return (
    <Typography component='span' sx={{ fontSize: 11, fontWeight: 700, color: undelivered ? '#B72136' : '#7A4F01' }}>
      +{lateDays}d {undelivered ? 'past EDD' : 'late'}
    </Typography>
  );
}

const buildColumns = (tab) => [
  {
    field: 'awb', headerName: 'Shipment', minWidth: 160, flex: 1, sortable: false,
    renderCell: ({ row }) => (
      <Box>
        <AwbLinkCell awb={row.awb} fallback={row.orderNo || '—'} sx={{ ...awbTextSx, whiteSpace: 'nowrap' }} />
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.orderNo}</Typography>
      </Box>
    ),
  },
  {
    field: 'lane', headerName: 'Pickup → Drop', minWidth: 160, flex: 1, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5 }} noWrap>{row.pickupCity || row.pickupPin || '—'} → {row.dropCity || row.dropPin || '—'}</Typography>
        <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{row.pickupPin} to {row.dropPin}</Typography>
      </Box>
    ),
  },
  {
    field: 'courierName', headerName: 'Carrier', minWidth: 130, flex: 1, sortable: false,
    renderCell: ({ row }) => <Typography sx={{ fontSize: 12.5 }} noWrap>{row.courierName || '—'}</Typography>,
  },
  {
    field: 'edd', headerName: 'EDD', minWidth: 140, flex: 0.9, sortable: false,
    renderCell: ({ row }) => (
      <Box>
        <Typography sx={{ fontSize: 12.5 }}>{fmtD(row.edd)}</Typography>
        {tab === 'D' && <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>delivered {fmtD(row.deliveredAt)}</Typography>}
        <LateTag breachType={row.breachType || tab} lateDays={row.lateDays} />{' '}
        {row.bucket && <Typography component='span' sx={{ fontSize: 11, color: 'text.secondary' }}>· {row.bucket}</Typography>}
      </Box>
    ),
  },
  {
    field: 'currentReasonLabel', headerName: 'Delay reason', minWidth: 220, flex: 1.8, sortable: false,
    renderCell: ({ row }) => (
      <Box sx={{ whiteSpace: 'normal', py: 0.5 }}>
        <Typography sx={{ fontSize: 12, color: row.currentReasonLabel ? 'text.primary' : 'text.disabled' }}>
          {row.currentReasonLabel || 'Not attributed yet. Prozo is reviewing it.'}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'owner', headerName: 'Attribution', minWidth: 130, flex: 0.9, sortable: false,
    // TODO(PTL): client reply - pending product decision. Client-attributable rows would get a reply control
    // here; today ops assigns them to a CAM who follows up with the client, so the merchant view stays read-only.
    renderCell: ({ row }) => <OwnerChip owner={row.owner} />,
  },
  ...(tab === 'D' ? [{
    field: 'closureStatus', headerName: 'Closure', minWidth: 120, sortable: false,
    renderCell: ({ row }) => (
      <Chip size='small' color={row.locked ? 'success' : 'error'} variant='outlined' label={row.locked ? 'Closed' : 'Pending'}
        sx={{ '& .MuiChip-label': { overflow: 'visible' } }} />
    ),
  }] : []),
];

// Filter controls: 40px tall, 13px text, labels above (same as the PoD Analytics filters).
const CONTROL_H = 40;
const fieldLabelSx = { fontSize: 12, fontWeight: 600, color: 'text.secondary', mb: 0.5, lineHeight: '18px', whiteSpace: 'nowrap' };
const fieldSx = { '& .MuiInputBase-root': { height: CONTROL_H, fontSize: 13, bgcolor: 'background.paper' } };
const TILE_ICONS = {
  NONE: <HourglassEmptyIcon />, PROZO: <BusinessIcon />, CLIENT: <PersonOutlineIcon />, EXTERNAL: <ThunderstormOutlinedIcon />,
};

function SelectField({ label, value, onChange, options, all, sx }) {
  return (
    <Box sx={{ minWidth: 170, flex: '1 1 170px', ...sx }}>
      <Typography sx={fieldLabelSx}>{label}</Typography>
      <TextField select size='small' fullWidth sx={fieldSx} value={value || ''} onChange={(e) => onChange(e.target.value)} SelectProps={{ displayEmpty: true }}>
        <MenuItem value=''>{all}</MenuItem>
        {options.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
      </TextField>
    </Box>
  );
}

/** Attribution tile: icon, label, count with its share of the tab, hint. Selected = tinted fill + outline. */
function OwnerTile({ owner, count, total, loading, selected, onClick }) {
  const o = EDD_OWNERS[owner];
  const share = total ? Math.round((100 * (count || 0)) / total) : 0;
  return (
    <ButtonBase onClick={onClick} aria-pressed={selected}
      sx={{
        p: 1.75, borderRadius: 2, border: `1px solid ${selected ? o.color : '#DFE3E8'}`, position: 'relative', overflow: 'hidden',
        display: 'block', textAlign: 'left', bgcolor: selected ? o.bg : 'background.paper',
        transition: 'box-shadow 0.15s, transform 0.15s',
        '&:hover': { boxShadow: '0 3px 10px rgba(0,0,0,0.08)', transform: 'translateY(-1px)' },
        '&::before': { content: '""', position: 'absolute', top: 0, left: 0, width: 4, height: '100%', bgcolor: o.accent },
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{o.label}</Typography>
        <Box sx={{ width: 28, height: 28, borderRadius: '8px', display: 'grid', placeItems: 'center', bgcolor: o.bg, color: o.color, '& svg': { fontSize: 16 } }}>
          {TILE_ICONS[owner]}
        </Box>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mt: 0.5 }}>
        {loading ? <Skeleton variant='text' width={48} sx={{ fontSize: 26 }} /> : (
          <Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.2, color: o.color, fontVariantNumeric: 'tabular-nums' }}>{count ?? 0}</Typography>
        )}
        {!loading && total > 0 && <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.secondary' }}>{share}%</Typography>}
      </Box>
      <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>{o.hint}</Typography>
    </ButtonBase>
  );
}

export default function EddBreaches() {
  const { enqueueSnackbar } = useSnackbar();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState('U');
  // ?owner=PROZO (from the Today's Shipments pendency tiles) opens the list on that attribution tile.
  const [kpi, setKpi] = useState(() => (EDD_OWNER_KEYS.includes(searchParams.get('owner')) ? searchParams.get('owner') : ''));
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(25);
  const [exporting, setExporting] = useState(false);

  // As in the Admin Panel the tile and the Attribution filter both apply. They are the same field, so together they
  // mean that one owner, or no rows when they differ (the merchant API takes a single owner).
  const conflict = !!(kpi && filters.owner && kpi !== filters.owner);
  const body = {
    tab,
    owner: kpi || filters.owner || undefined,
    bucket: filters.bucket || undefined,
    q: filters.q ? filters.q.trim() : undefined,
  };
  // The merchant picked in the panel. The server uses it only for Proship admins (their token keeps the home
  // merchant); merchant admins and users always get their token's merchant.
  const merchantOid = getMerchant()?.id || '';
  const scope = merchantOid ? `?merchantOid=${encodeURIComponent(merchantOid)}` : '';
  const bodyKey = JSON.stringify(body);
  const summary = usePtlApi(() => getGeneric(`${BASE}/summary`, merchantOid ? { tab, merchantOid } : { tab }), `${tab}|${merchantOid}`);
  const cases = usePtlApi(() => postGeneric(`${BASE}/cases${scope}`, { ...body, page, size }), `${bodyKey}|${page}|${size}|${merchantOid}`,
    { skip: summary.missing || conflict });

  // A new tab starts clean except the attribution and AWB filters (Admin Panel rule).
  const changeTab = (_e, v) => { setTab(v); setKpi(''); setFilters((f) => ({ ...f, bucket: '' })); setDraft((d) => ({ ...d, bucket: '' })); setPage(0); };
  const pickTile = (o) => { setKpi(kpi === o ? '' : o); setPage(0); };
  const pickMatrix = ({ bucket, owner }) => {
    const next = { ...filters, bucket, owner };
    setFilters(next); setDraft(next); setKpi(''); setPage(0);
  };
  const applyFilters = () => { setFilters(draft); setPage(0); };
  const resetFilters = () => { setFilters(EMPTY_FILTERS); setDraft(EMPTY_FILTERS); setKpi(''); setPage(0); };
  const clearChip = (k) => {
    if (k === 'kpi') setKpi('');
    else { setFilters((f) => ({ ...f, [k]: '' })); setDraft((d) => ({ ...d, [k]: '' })); }
    setPage(0);
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await downloadZip(`${BASE}/cases/export${scope}`, body);
      await saveCsvAsXlsx(res.data, `PTL_EDD_Breaches_${tab === 'D' ? 'Delivered_Late' : 'Undelivered'}`);
      enqueueSnackbar('Export ready', { variant: 'success' });
    } catch (e) {
      enqueueSnackbar(errorText(e, 'Export failed. Please try again.'), { variant: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const tiles = summary.data?.tiles || {};
  const data = conflict ? { rows: [], total: 0, tabTotal: cases.data?.tabTotal } : cases.data;
  const rows = data?.rows || [];
  const activeChips = [
    filters.bucket && { k: 'bucket', label: `Ageing: ${EDD_BUCKET_LABEL[filters.bucket]}` },
    filters.owner && { k: 'owner', label: `Attribution: ${EDD_OWNERS[filters.owner]?.short}` },
    kpi && { k: 'kpi', label: `Tile: ${EDD_OWNERS[kpi]?.short}` },
  ].filter(Boolean);

  const tabTotal = summary.data?.tabCounts?.[tab] ?? 0;

  return (
    <Box sx={pageContainerSx}>
      <PageTitle subTitle='Every breach sits in one of four states: not attributed, Prozo, Client or Force majeure. Pick one to see only those shipments.'>
        EDD Breach Attribution
      </PageTitle>

      {summary.missing ? (
        <ApiNeededNotice
          title='EDD breaches are not available for this login yet'
          summary='This page reads the merchant EDD breach API, which is not open for this login.'
          endpoint={`GET /api/${BASE}/summary · POST /api/${BASE}/cases · POST /api/${BASE}/cases/export`}
          footnote='The page fills in on its own once these APIs are live.'
        />
      ) : (
        <>
          <Tabs value={tab} onChange={changeTab} sx={{ ...tabBarSx, mb: 2 }}>
            {EDD_TABS.map((t) => (
              <Tab key={t.key} value={t.key} label={`${t.label} (${summary.data?.tabCounts?.[t.key] ?? '…'})`} />
            ))}
          </Tabs>

          {summary.error && <Alert severity='error' sx={{ mb: 2 }}>{summary.error}</Alert>}

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' }, gap: 2, mb: 2 }}>
            {EDD_OWNER_KEYS.map((o) => (
              <OwnerTile key={o} owner={o} count={tiles[o]} total={tabTotal} loading={summary.loading}
                selected={kpi === o} onClick={() => pickTile(o)} />
            ))}
          </Box>

          <Card variant='outlined' sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent sx={{ pb: '16px !important' }}>
              <AgeingMatrix tab={tab} matrix={summary.data?.matrix} loading={summary.loading && !summary.data}
                selected={{ bucket: filters.bucket, owner: filters.owner }} onPick={pickMatrix} />
            </CardContent>
          </Card>

          <Card variant='outlined' sx={{ borderRadius: 2 }}>
            {/* filters, count, chips and export together on top of the list */}
            <Box component='form' onSubmit={(e) => { e.preventDefault(); applyFilters(); }}
              sx={{ display: 'flex', gap: 2, alignItems: 'flex-end', flexWrap: 'wrap', px: 2, pt: 2, pb: 1.5 }}>
              <SelectField label='Attribution' value={draft.owner} onChange={(v) => setDraft((d) => ({ ...d, owner: v }))}
                options={EDD_OWNER_KEYS.map((o) => ({ value: o, label: EDD_OWNERS[o].label }))} all='All attribution' />
              <SelectField label={tab === 'U' ? 'Days past EDD' : 'Days late'} value={draft.bucket} onChange={(v) => setDraft((d) => ({ ...d, bucket: v }))}
                options={EDD_BUCKETS} all='All ageing' />
              <Box sx={{ minWidth: 240, flex: '2 1 260px' }}>
                <Typography sx={fieldLabelSx}>AWB number(s)</Typography>
                <TextField size='small' fullWidth sx={fieldSx} placeholder='Search AWB, comma separated for many' value={draft.q}
                  onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))} />
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button type='submit' variant='contained' sx={{ textTransform: 'none', height: CONTROL_H, boxShadow: 'none' }}>Filter</Button>
                <Button variant='outlined' onClick={resetFilters} sx={{ textTransform: 'none', height: CONTROL_H }}>Reset</Button>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2, py: 1.25, flexWrap: 'wrap', borderTop: '1px solid #DFE3E8', bgcolor: '#F9FAFB' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: 'text.primary' }}>
                {data && !cases.loading
                  ? (data.tabTotal == null || data.total === data.tabTotal ? `${data.total} shipments` : `${data.total} of ${data.tabTotal} shipments`)
                  : 'Loading…'}
              </Typography>
              {activeChips.map((c) => (
                <Chip key={c.k} size='small' label={c.label} onDelete={() => clearChip(c.k)}
                  sx={{ bgcolor: 'background.paper', border: '1px solid #DFE3E8', '& .MuiChip-label': { textTransform: 'none' } }} />
              ))}
              <Box sx={{ ml: 'auto' }}>
                <LoadingButton size='small' variant='outlined' startIcon={<DownloadOutlinedIcon />} loading={exporting} disabled={!data?.total}
                  onClick={exportCsv} sx={{ textTransform: 'none', bgcolor: 'background.paper' }}>Export</LoadingButton>
              </Box>
            </Box>
            {cases.error && !cases.missing ? (
              <Alert severity='error' sx={{ m: 2 }}>{cases.error}</Alert>
            ) : (
              <ProzoDataGrid
                columns={buildColumns(tab)}
                rows={rows}
                rowCount={data?.total || 0}
                paginationMode='server'
                pagination
                page={page}
                setPage={setPage}
                sizePerPage={size}
                setSizePerPage={(s) => { setSize(s); setPage(0); }}
                loading={cases.loading}
                getRowId={(r) => r.id || r.awb}
                rowHeight={64}
                boxShadow={0}
              />
            )}
          </Card>
        </>
      )}
    </Box>
  );
}
